import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { Select } from "components/base/Form";
import Page from "components/base/Page";
import Spinner from "components/base/Spinner";
import { useModals } from "ModalProvider";
import CreatePlan from "pages/orderScheduler/CreatePlan";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { formatCountry } from "utils/string";
import ForecastProductView from "./ForecastProductView";
import { FETCH_FCST_OVERVIEW } from "./graphql";

const SingleMonthForecastPage = () => {
  const { month, mode, forecastCategoryId } = useParams();
  const modal = useModals();
  const navigate = useNavigate();
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const { loading, error, data } = useQuery(FETCH_FCST_OVERVIEW, {
    variables: { mode, forecastCategoryId },
    fetchPolicy: "network-only",
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const parsedForecast =
    typeof data.salesGetForecast === "string"
      ? JSON.parse(data.salesGetForecast)
      : data.salesGetForecast || {};
  const forecastCategory = parsedForecast.forecast_categories.find(
    (item) => String(item.id) === String(forecastCategoryId),
  );

  console.log("parsedForecast", parsedForecast);

  const products = parsedForecast.products
    .map((item) => {
      const monthData = item.cells?.[month] || {};
      const forecastQty =
        forecastCategoryId && monthData[forecastCategoryId] != null
          ? Number(monthData[forecastCategoryId] || 0)
          : Object.values(monthData).reduce(
              (total, qty) => total + Number(qty || 0),
              0,
            );
      return {
        ...(item.product || {}),
        forecastQty,
      };
    })
    .filter((product) => product.forecastQty > 0);

  const countryOptions = Array.from(
    new Set(
      products.map((product) => product.countryCodeOfOrigin || "UNKNOWN"),
    ),
  ).sort();
  const hasMultipleCountries = countryOptions.length > 1;
  const defaultCountryCode = hasMultipleCountries
    ? countryOptions.includes("CN")
      ? "CN"
      : countryOptions[0]
    : countryOptions[0] || "ALL";
  const effectiveCountryCode = countryOptions.includes(selectedCountryCode)
    ? selectedCountryCode
    : defaultCountryCode;
  const planProducts =
    effectiveCountryCode === "ALL"
      ? products
      : products.filter(
          (product) =>
            (product.countryCodeOfOrigin || "UNKNOWN") === effectiveCountryCode,
        );

  function showCreatePlan() {
    let planType = "AB_LTD_SALES_ORDER";
    let customer;

    if (forecastCategory.mode === "AB") {
      if (forecastCategory.customer) {
        planType = "AB_LTD_SALES_ORDER";
        customer = forecastCategory.customer;
      } else if (forecastCategory.stockCategory) {
        planType = "AB_INTERNAL_TRANSFER";
      }
    } else if (forecastCategory.mode === "INC") {
      if (forecastCategory.name === "Amazon") {
        planType = "INC_AMAZON_SELLER_CENTRAL_ORDER";
      } else if (forecastCategory.name === "FSI") {
        planType = "INC_STOCK_ORDER";
      }
    }

    if (!planType) {
      window.alert("Cannot create a plan for this forecast category.");
      return;
    }

    modal.present({
      title: "Create a Plan",
      center: true,
      maxWidth: "max-w-3xl",
      children: (
        <CreatePlan
          initialValues={{
            name: `${forecastCategory.name} Order Plan - ${month}`,
            planType,
            customer,
            lines: planProducts.map((product) => ({
              productId: product.id,
              qty: product.forecastQty,
            })),
          }}
          skipValidateCustomer
          onCreatedPlan={(plan) =>
            navigate(`/order-scheduler/plans/${plan.id}`)
          }
        />
      ),
    });
  }

  return (
    <Page
      testing
      backTo="./../.."
      title={`${mode} / Sales Forecast / ${month} / ${forecastCategory.name || "All Warehouses"}`}
    >
      <div className="p-6 grid grid-cols-12 gap-6 flex-1 overflow-auto items-start">
        <div className="col-span-8">
          <div className="card p-0 overflow-auto text-xs">
            <table className="w-full">
              <thead>
                <tr className="sticky top-0 backdrop-blur bg-gray-200 bg-opacity-80 dark:bg-gray-800 dark:bg-opacity-80 border-b border-gray-200 dark:border-gray-700 z-10">
                  <th className="px-4 py-3 text-left whitespace-nowrap">
                    Product ({products.length})
                  </th>
                  <th className="px-4 py-3 text-right whitespace-nowrap">
                    Forecast QTY
                  </th>
                </tr>
              </thead>
              <tbody>
                {products.length === 0 && (
                  <tr>
                    <td
                      colSpan={2}
                      className="px-4 py-6 text-center text-gray-500"
                    >
                      No forecasted products for this month.
                    </td>
                  </tr>
                )}
                {products.map((product) => (
                  <tr
                    key={product.id}
                    className="border-y border-gray-100 dark:border-gray-700"
                  >
                    <td
                      className={`px-4 py-2 ${
                        hasMultipleCountries &&
                        effectiveCountryCode !== "ALL" &&
                        (product.countryCodeOfOrigin || "UNKNOWN") !==
                          effectiveCountryCode
                          ? "opacity-30"
                          : ""
                      }`}
                    >
                      <ForecastProductView product={product} />
                    </td>
                    <td className="px-4 py-2 text-right font-semibold whitespace-nowrap">
                      {product.forecastQty.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="col-span-4 space-y-6">
          {products.length > 0 && (
            <div className="card">
              <h5>Planning</h5>
              <div className="mt-2 space-y-1">
                <div>
                  You can convert this forecast products into a plan in Order
                  Scheduler.
                </div>

                {hasMultipleCountries && (
                  <div className="text-xs text-gray-500 pt-1">
                    Forecast products include multiple countries. Please choose
                    a product country before creating the plan.
                  </div>
                )}
              </div>

              {hasMultipleCountries && (
                <div className="mt-4">
                  <label className="block text-xs opacity-70 mb-1">
                    Product Country
                  </label>
                  <Select
                    className="-mx-1"
                    value={effectiveCountryCode}
                    onChange={(e) => setSelectedCountryCode(e.target.value)}
                  >
                    {countryOptions.map((countryCode) => (
                      <option key={countryCode} value={countryCode}>
                        {countryCode === "UNKNOWN"
                          ? "Unknown"
                          : `${formatCountry(countryCode, true, true)} (${countryCode})`}{" "}
                        (
                        {
                          products.filter(
                            (product) =>
                              (product.countryCodeOfOrigin || "UNKNOWN") ===
                              countryCode,
                          ).length
                        }
                        )
                      </option>
                    ))}
                  </Select>
                </div>
              )}

              <div className="flex mt-4 -mx-1">
                <Button
                  primary
                  onClick={showCreatePlan}
                  disabled={planProducts.length === 0}
                >
                  Create Plan
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
};

export default SingleMonthForecastPage;
